import React, { useEffect, useRef, useState } from 'react';
import './SkillCard.less';

// 仅在当前页面生命周期内记录播放状态：刷新会清空，滚动/翻页回来不再重播。
const playedRecommendCards = new Set<string>();

const goldenParticles = [
  { left: '28%', top: '62%', delay: '0.40s', duration: '0.96s', x: '-18px', y: '-42px', size: '5px' },
  { left: '37%', top: '52%', delay: '0.50s', duration: '1.10s', x: '18px', y: '-58px', size: '7px' },
  { left: '45%', top: '64%', delay: '0.57s', duration: '0.88s', x: '30px', y: '-36px', size: '4px' },
  { left: '52%', top: '42%', delay: '0.63s', duration: '1.08s', x: '-26px', y: '-52px', size: '6px' },
  { left: '60%', top: '58%', delay: '0.68s', duration: '1.16s', x: '24px', y: '-62px', size: '5px' },
  { left: '66%', top: '36%', delay: '0.74s', duration: '0.92s', x: '35px', y: '-32px', size: '4px' },
  { left: '72%', top: '54%', delay: '0.80s', duration: '1.05s', x: '-12px', y: '-48px', size: '7px' },
  { left: '78%', top: '44%', delay: '0.86s', duration: '0.98s', x: '20px', y: '-42px', size: '4px' }
];

export const formatNumber = (num: number) => {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return num.toString();
};

interface SkillCardProps {
  skill: any;
  index: number;
}

const SkillCard: React.FC<SkillCardProps> = ({ skill, index }) => {
  const skillDescription = skill.skillDescription || skill.info || skill.description || '暂无描述';
  const recommend = skill.skillRecommend;
  const recommendClass = recommend === '交付龙' ? 'delivery' : recommend === '价值龙' ? 'value' : 'efficiency';
  const cardKey = String(skill.id || skill.name);
  const isRecommendCard = Boolean(recommend);
  const cardRef = useRef<HTMLDivElement>(null);
  const [hasPlayed, setHasPlayed] = useState(() => !isRecommendCard || playedRecommendCards.has(cardKey));
  const [playIntro, setPlayIntro] = useState(false);

  useEffect(() => {
    if (!isRecommendCard || hasPlayed || !cardRef.current) return;

    const play = () => {
      if (playedRecommendCards.has(cardKey)) {
        setHasPlayed(true);
        return;
      }
      playedRecommendCards.add(cardKey);
      setHasPlayed(true);
      setPlayIntro(true);
    };
    if (typeof IntersectionObserver === 'undefined') {
      play();
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        play();
        observer.disconnect();
      }
    }, { threshold: 0.18 });
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [cardKey, hasPlayed, isRecommendCard]);

  useEffect(() => {
    if (!playIntro) return;
    const timer = window.setTimeout(() => setPlayIntro(false), 1500);
    return () => window.clearTimeout(timer);
  }, [playIntro]);

  const handleClick = () => {
    window.location.href = `/skill-market/detail/${skill.name}`;
  };

  return (
    <div
      ref={cardRef}
      className={`skill-card ${isRecommendCard ? 'special-card' : 'normal-card'} ${hasPlayed ? 'visible' : ''} ${playIntro ? 'play-intro' : ''}`}
      style={isRecommendCard ? undefined : { animationDelay: `${index * 0.05}s` }}
      onClick={handleClick}
    >
      {isRecommendCard && playIntro && (
        <div className="golden-particles" aria-hidden="true">
          {goldenParticles.map((particle, particleIndex) => (
            <span
              key={particleIndex}
              className="golden-particle"
              style={{
                left: particle.left,
                top: particle.top,
                width: particle.size,
                height: particle.size,
                animationDelay: particle.delay,
                animationDuration: particle.duration,
                '--particle-x': particle.x,
                '--particle-y': particle.y
              } as React.CSSProperties}
            />
          ))}
        </div>
      )}
      <div className="skill-card-header">
        <h3 className="skill-name" title={skill.name}>
          {skill.name || '未命名技能'}
        </h3>
        {recommend && (
          <span className={`skill-recommend-badge ${recommendClass}`} title={skill.skillRecommendRemark || recommend}>
            <span className="badge-label">{recommend}</span>
          </span>
        )}
        {skill.version && (
          <span className="skill-version">v{skill.version}</span>
        )}
      </div>

      <p className="skill-description" title={skillDescription}>
        {skillDescription}
      </p>

      <div className="skill-stats">
        <span className="stat-item">
          <span className="stat-icon">📥</span>
          <span className="stat-value">{formatNumber(skill.download || 0)}</span>
        </span>
        <span className="stat-item">
          <span className="stat-icon">✨</span>
          <span className="stat-value">{formatNumber(skill.skill_active_count || 0)}</span>
        </span>
      </div>

      <div className="skill-author">
        <span className="author-text">by @{skill.creatorName || skill.author || 'anonymous'}</span>
        <span className="author-divider">·</span>
        <span className="update-time">更新时间 {skill.latestTime || '--'}</span>
      </div>
    </div>
  );
};

export default SkillCard;
